import { uploadEngine, type UploadEngine, type UploadEngineSnapshot } from "@/upload";

// 업로드가 사용자의 조치를 기다릴 때 브라우저 탭 제목으로 알린다.
//
// Owns:
// - 알림의 조건과 문구. 조건은 하나다: 단절이 한도까지 이어져 엔진이 일시정지한 잡이 있다
//   (`paused` 이고 `connection === "lost"`). 그 잡은 사용자가 재개해야 이어진다.
// - 원래 제목의 보존과 복원.
//
// Does not own:
// - 연결 상태의 판정(UploadEngine), 업로드 창 안의 상태 문구(uploadJobStatus).
//
// 연결을 기다리는 동안(`waiting`)에는 제목을 바꾸지 않는다. 그때는 사용자가 할 일이 없다. 짧은
// 단절마다 제목이 바뀌면 알림의 뜻이 약해진다. 브라우저 알림(Notification)도 쓰지 않는다.

const pausedTitlePrefix = "업로드 일시정지 · ";

type EngineSource = Pick<UploadEngine, "subscribe" | "getSnapshot">;
type TitleTarget = { title: string };

export class UploadTabTitle {
  private stop: (() => void) | null = null;

  constructor(
    private readonly engine: EngineSource,
    // start 시점에 읽는다. 모듈을 평가할 때는 document 가 없을 수 있다(node 테스트).
    private readonly target: () => TitleTarget,
  ) {}

  /** 엔진을 구독하고 제목을 맞춘다. 이미 시작했으면 아무것도 하지 않는다. */
  start(): void {
    if (this.stop !== null) {
      return;
    }
    const target = this.target();
    const baseTitle = target.title;
    const apply = (): void => {
      const next = needsUserResume(this.engine.getSnapshot())
        ? pausedTitlePrefix + baseTitle
        : baseTitle;
      // 엔진은 진행 이벤트마다 알린다. 값이 같으면 다시 쓰지 않는다.
      if (target.title !== next) {
        target.title = next;
      }
    };
    const unsubscribe = this.engine.subscribe(apply);
    apply();
    this.stop = () => {
      unsubscribe();
      target.title = baseTitle;
    };
  }

  /** 구독을 끊고 원래 제목으로 되돌린다. */
  dispose(): void {
    this.stop?.();
    this.stop = null;
  }
}

// 재개할 수 있는 잡만 센다. 그래서 연결 표시와 함께 잡 상태도 본다.
function needsUserResume(snapshot: UploadEngineSnapshot): boolean {
  return snapshot.jobs.some((job) => job.status === "paused" && job.connection === "lost");
}

// 앱 범위 싱글턴. 세션 수명에 맞춰 AppRoot 가 start·dispose 한다.
export const uploadTabTitle = new UploadTabTitle(uploadEngine, () => document);
